/**
 * [spec-013 T003] A seção "Atalhos" do menu do `/` — atalhos de NEGÓCIO
 * (`/panorama`, `/pré-reunião <cliente>`) que despacham um TURNO, ao
 * contrário dos 8 comandos de hoje (`send-message-catalogo-comandos-
 * mixin.js`), que são ações locais do composer.
 *
 * Extraído em arquivo próprio pelo mesmo motivo do mixin de catálogo: o
 * `send-message.vue` está no teto de linhas do repo, e este mixin não toca
 * nele — é `send-message-catalogo-comandos-mixin.js` quem o incorpora
 * (`mixins: [sendMessageAtalhosMixin]`) e concatena `catalogoDeAtalhos` ao
 * fim de `catalogoDeComandos`. Aditivo: os 8 comandos atuais não mudam.
 *
 * Não é dado estático — lê `this.value` (prop do HOST, `send-message.vue`)
 * e `this.$listeners`, no mesmo espírito do mixin de catálogo.
 *
 * ## Por que um evento próprio (`executar-atalho`), nunca `send`
 *
 * Os dois composers que hospedam este menu (`view-chat.vue:2372,2447`)
 * ligam `send` a `onSendMessage` → `enviarMensagem`, que faz
 * `.trim()` no argumento (`view-chat.vue:1612`) — um OBJETO ali levanta
 * `TypeError` antes do POST. E `enviarMensagem` pode desviar para
 * `POST /runs` com o Modo Agente ligado (`despacharMensagem`,
 * `view-chat.vue:1666-1672` — "a única bifurcação do envio"), o que faria o
 * atalho perder o desenho síncrono do progresso. Por isso o atalho nunca
 * passa por `send`: emite `executar-atalho`, com handler dedicado no host,
 * que fala com `/chat` direto via `apiChatAtalhos.sendAtalho()`.
 *
 * ## Selecionar vira CHIP, Enter DESPACHA
 *
 * Clicar/Tab/Enter num atalho do menu enviava na hora, sem chance de digitar o
 * cliente do `/pré-reunião` nem de desistir. Selecionar agora guarda o atalho em
 * `atalhoSelecionado` — o composer o desenha como o MESMO chip do módulo
 * (`comando-chip`) — e deixa na caixa só o que vinha depois do comando (o
 * argumento). O turno sai no ENVIO (`despacharAtalhoDigitado`, chamado por
 * `send-message.vue::onSend`), com a caixa inteira como argumento. Digitar
 * `/panorama` à mão continua valendo: sem chip, o token casando o catálogo
 * despacha do mesmo jeito. O texto abaixo (US-1) descreve o comportamento
 * ANTERIOR da spec-013.
 *
 * ## (anterior) Selecionar sempre DESPACHA — nunca insere o prompt na caixa (US-1)
 *
 * "WHEN o usuário seleciona um atalho de negócio, THE system SHALL
 * despachar um turno com o comando estruturado — nunca inserir o texto do
 * prompt na caixa" (spec.md, US-1). Um atalho com argumento ausente
 * (`/pré-reunião` sem nome) despacha do mesmo jeito — é o SERVIDOR quem
 * pergunta pelo cliente em vez de assumir (edge case do US-1), não o front.
 *
 * ## [spec-013 T016] Um vocabulário só — o do produtor
 *
 * Até aqui este mixin consumia `{slug, rotulo, descricao, icone, aliases}`
 * — forma ESPECULATIVA escrita nesta mesma task, ANTES de `GET
 * /v2/api/ia-crm/atalhos` existir. Quando o endpoint fechou (T013/T014), o
 * contrato real (`contracts/api.yaml`, `core/atalhos/catalogo.py::Atalho.
 * para_payload()`) saiu `{id, label, description, pedeArgumento,
 * placeholderArgumento}` — e `view-chat.vue::carregarAtalhos()` passou a
 * TRADUZIR um vocabulário no outro, escondendo a tradução numa view e
 * perdendo `pedeArgumento`/`placeholderArgumento` no caminho (não havia
 * onde eles pousarem na forma antiga). T016 remove a tradução: este mixin
 * agora consome DIRETO o vocabulário do contrato. `icone`/`aliases`
 * nunca existiram no contrato — os fallbacks abaixo (`ICONE_PADRAO`, `[]`)
 * continuam cobrindo a ausência deles, sem inventar campo novo no servidor.
 */
import { normalizeText } from '@utils/Format'
import {
  iconeDoAtalho,
  rotuloDeComandoDoModulo,
} from '@modules/aawz-intelligence-v2/utils/comando-na-fala'

// Mesmo caractere que abre o menu em `send-message.vue` (`COMANDO_DE_
// FERRAMENTAS`). Duplicado de propósito: importar a constante privada do
// host acoplaria os dois arquivos por uma string solta — as duas cópias
// documentam a MESMA decisão (o `/` do composer), não duas decisões
// diferentes que podem divergir.
const COMANDO_DE_FERRAMENTAS = '/'

const ROTULO_DA_SECAO = 'Atalhos'

export const sendMessageAtalhosMixin = {
  props: {
    /**
     * Catálogo de atalhos de negócio, JÁ FILTRADO por papel NO SERVIDOR
     * (`GET /v2/api/ia-crm/atalhos`, RBAC de `limiares_office.py`) — o
     * front só desenha (plan.md §2.1). `[]` = a seção some por completo
     * (ver `catalogoDeAtalhos`).
     *
     * [spec-013 T016] Cada item: `{id, label, description, pedeArgumento,
     * placeholderArgumento}` — EXATAMENTE `Atalho.para_payload()`
     * (`core/atalhos/catalogo.py`), sem tradução nenhuma no caminho.
     */
    atalhos: {
      type: Array,
      default: function () {
        return []
      },
    },
  },
  data() {
    return {
      // O atalho escolhido no menu, desenhado como chip. `null` = nenhum.
      atalhoSelecionado: null,
    }
  },
  computed: {
    /**
     * O primeiro token depois da `/` — usado para casar contra `atalho.id`
     * (`atalhoAtivo`, abaixo) e contra `aliases`. `''` fora do modo comando.
     *
     * ⚠️ NÃO é `filtroDeFerramentas` (que já virou só o primeiro token pela
     * correção cirúrgica desta mesma task) — este computed é independente
     * dele por design: o mixin não pode depender de um computed que vive no
     * HOST e que outra sessão pode reformular amanhã.
     */
    tokenDoAtalho() {
      const valor = String(this.value || '')
      if (!valor.startsWith(COMANDO_DE_FERRAMENTAS)) return ''
      const resto = valor.slice(COMANDO_DE_FERRAMENTAS.length)
      const indiceDoEspaco = resto.indexOf(' ')
      return indiceDoEspaco === -1 ? resto : resto.slice(0, indiceDoEspaco)
    },
    /**
     * O que vem DEPOIS do primeiro espaço — o argumento livre de um atalho
     * como `/pré-reunião Marcelo Pontes` (`"Marcelo Pontes"`). `''` sem
     * argumento digitado.
     */
    argumentoDoAtalho() {
      // Com o chip, a caixa inteira é o argumento.
      if (this.atalhoSelecionado) return String(this.value || '').trim()
      const valor = String(this.value || '')
      if (!valor.startsWith(COMANDO_DE_FERRAMENTAS)) return ''
      const resto = valor.slice(COMANDO_DE_FERRAMENTAS.length)
      const indiceDoEspaco = resto.indexOf(' ')
      if (indiceDoEspaco === -1) return ''
      return resto.slice(indiceDoEspaco + 1).trim()
    },
    /**
     * A seção "Atalhos" do menu do `/` — só existe com catálogo não vazio
     * **e** com a superfície hospedeira escutando `executar-atalho` (US-1:
     * "se a superfície hospedeira não trata turno próprio — `agent-chat`,
     * `fee-fixo-ia-especialista` —, omitir os atalhos do menu". Mesma régua
     * do `mostrar` de "Nova conversa" em
     * `send-message-catalogo-comandos-mixin.js`, que testa
     * `this.$listeners['nova-conversa']`).
     *
     * O primeiro item é um TÍTULO não-clicável (`disabled: true`), no
     * mesmo padrão da linha `SEM_RESULTADO` de `composer-ferramentas.vue` —
     * não há conceito de cabeçalho de seção no componente, e criar um
     * componente novo está fora do escopo desta task.
     */
    catalogoDeAtalhos() {
      if (!this.atalhos || !this.atalhos.length) return []
      if (!this.$listeners['executar-atalho']) return []
      const titulo = {
        mostrar: true,
        label: ROTULO_DA_SECAO,
        disabled: true,
      }
      const itens = this.atalhos.map(this.comandoDoAtalho)
      return [titulo].concat(itens)
    },
    /**
     * [spec-013 T016] O atalho do catálogo cujo `id` bate com
     * `tokenDoAtalho` — usado só para achar a DICA de argumento
     * (`dicaDeArgumentoDoAtalho`, abaixo). `null` sem token, ou sem nenhum
     * atalho casando.
     *
     * `normalizeText` (`@utils/Format` — mesmo util que
     * `composer-ferramentas.vue::semAcento` usa para o filtro do menu, para
     * as duas comparações nunca divergirem) sem acento/caixa: `atalho.id`
     * já É a forma sem acento (`pre-reuniao`), então normalizar só o TOKEN
     * casa tanto quem digita o slug quanto quem digita o rótulo acentuado
     * (`/pré-reunião`).
     */
    atalhoAtivo() {
      if (this.atalhoSelecionado) return this.atalhoSelecionado
      if (!this.tokenDoAtalho) return null
      const alvo = normalizeText(this.tokenDoAtalho)
      return (
        (this.atalhos || []).find(
          (atalho) => normalizeText(atalho.id || '') === alvo
        ) || null
      )
    },
    /**
     * O chip da caixa: o atalho escolhido vence o módulo ativo (é a escolha
     * mais recente, e é ela que o Enter despacha). `null` sem nenhum dos dois.
     * Lê `ferramentaAtiva`, prop do HOST.
     */
    chipDoComposer() {
      if (this.atalhoSelecionado) {
        return {
          icone: iconeDoAtalho(this.atalhoSelecionado),
          rotulo: this.atalhoSelecionado.label,
        }
      }
      const modulo = this.ferramentaAtiva
      if (!modulo) return null
      return { icone: modulo.icone, rotulo: rotuloDeComandoDoModulo(modulo) }
    },
    /**
     * O envio deste composer vai despachar um ATALHO (e não `send`)?
     *
     * Mesmas duas condições de `despacharAtalhoDigitado` — a superfície
     * escuta `executar-atalho` e há atalho ativo (chip escolhido ou token
     * digitado casando o catálogo). Extraído para computed porque o HOST
     * precisa da mesma resposta ANTES do clique, no `canSend`.
     */
    despachaAtalhoNoEnvio() {
      if (!this.$listeners['executar-atalho']) return false
      return Boolean(this.atalhoAtivo)
    },
    /**
     * Este envio vai partir como ATALHO tendo anexo na fila?
     *
     * ★ [T014/T015, spec-009 W6, 20/09/2026 — o computed se chamava
     * `atalhoDescartariaAnexo` e TRAVAVA o envio no `canSend` do host.]
     *
     * O turno de atalho não tem campo de anexo (`executar-atalho` carrega
     * `{slug, label, argumento}`; `apiChatAtalhos.sendAtalho` manda
     * `comando: {slug, argumento}`) — e a razão de fundo é maior que o
     * transporte: ele fecha SEM MODELO (`usecase_ia_crm_chat.py:2416-2425` —
     * resolve a skill, chama UMA tool e retorna antes do miolo supervisor).
     * Um anexo existe para ser LIDO por um modelo; levá-lo ali seria entrega
     * a um consumidor inexistente.
     *
     * Por isso o desenho não é transportar nem travar, e sim **avisar e
     * preservar** (`contracts/atalho-com-anexo.md`): o atalho parte, o anexo
     * FICA na fila (o `onSend` do host sai por `despacharAtalhoDigitado()`
     * ANTES de `removerAnexosEnviados()`), e o composer diz isso no aviso
     * abaixo do campo e no `title` do botão
     * (`avisoDeAnexo`/`rotuloBotaoAcao`, host).
     *
     * ⚠️ Não voltar a usá-lo como trava: o bloqueio de 17/09 é exatamente o
     * que o CTO mandou tirar, e um `canSend` false aqui devolve o composer
     * preso com o botão apagado.
     */
    atalhoNaoLevaAnexo() {
      return Boolean(this.temAnexos && this.despachaAtalhoNoEnvio)
    },
    // Com o chip a caixa fica vazia: é ali que a dica do argumento cabe.
    placeholderDoAtalhoSelecionado() {
      const atalho = this.atalhoSelecionado
      if (!atalho || !atalho.pedeArgumento) return ''
      return atalho.placeholderArgumento || ''
    },
    /**
     * [spec-013 T016] A dica de argumento do atalho ativo — consumida pelo
     * HOST (`send-message.vue::textoRodape`, a linha de hint do rodapé do
     * composer, sempre visível independente do conteúdo da caixa; o
     * `:placeholder` nativo do campo não serviria aqui porque só aparece
     * com o campo VAZIO, e o atalho ativo por definição tem `/algo` digitado).
     *
     * `null` sem atalho ativo, sem `pedeArgumento`, ou sem
     * `placeholderArgumento` no catálogo — degradação graciosa de
     * propósito (US-1 não exige a dica, só o despacho): o servidor parar de
     * mandar o campo faz a dica sumir, nunca quebra o atalho
     * (`dispararAtalho`, abaixo, não lê este computed).
     */
    dicaDeArgumentoDoAtalho() {
      const atalho = this.atalhoAtivo
      if (!atalho || !atalho.pedeArgumento) return null
      return atalho.placeholderArgumento || null
    },
  },
  methods: {
    // Um atalho do catálogo → item do menu do `/`. `pedeArgumento`/
    // `placeholderArgumento` viajam no item por completude do contrato
    // (`design-system`/`composer-inline-select.vue` ignoram campo que não
    // conhecem — mesmo tratamento que `aliases` já recebe ali), mesmo sem
    // consumidor de tela hoje além de `atalhoAtivo`/`dicaDeArgumentoDoAtalho`.
    comandoDoAtalho(atalho) {
      return {
        mostrar: true,
        label: atalho.label,
        description: atalho.description || '',
        icon: iconeDoAtalho(atalho),
        aliases: atalho.aliases || [],
        pedeArgumento: Boolean(atalho.pedeArgumento),
        placeholderArgumento: atalho.placeholderArgumento || null,
        onClick: () => {
          this.preencherAtalho(atalho)
        },
      }
    },
    /**
     * Limpa a caixa e emite o comando estruturado (nunca `send` — ver
     * cabeçalho do arquivo). O argumento livre viaja como está, inclusive
     * vazio (`null`): quem decide se pede o nome do cliente é o backend.
     *
     * [spec-013 T016] `slug` aqui é o campo do EVENTO `executar-atalho`
     * (contrato com `view-chat.vue::executarAtalho()` → `apiChatAtalhos.
     * sendAtalho({slug, ...})`, T001/T003) — o VALOR vem de `atalho.id`, o
     * campo do catálogo real.
     *
     * ★ [spec-013 T024] `label` viaja junto, e é o campo do CATÁLOGO do
     * servidor (`Atalho.para_payload()`): é dele que sai, do outro lado do
     * transporte, a bolha do usuário no fio — e o backend monta a MESMA fala
     * com o MESMO `atalho.label` (`core/atalhos/turno.py::_fala_do_comando`).
     * Recompor a string no transporte (`'/' + slug`) daria `/pre-reuniao`
     * onde o banco guardou `/pré-reunião`, e o F5 trocaria a bolha debaixo
     * do usuário.
     */
    // Selecionar no menu: vira chip e a caixa fica só com o que vinha depois
    // do comando digitado (`/pré-reunião Ana` → chip + `Ana`). Não envia.
    preencherAtalho(atalho) {
      const restoDigitado = this.tokenDoAtalho ? this.argumentoDoAtalho : ''
      this.atalhoSelecionado = atalho
      this.$emit('input', restoDigitado)
      const menu = this.$refs && this.$refs.ferramentas
      if (menu && typeof menu.fechar === 'function') menu.fechar()
      this.$nextTick(() => {
        if (typeof this.focarCampo === 'function') this.focarCampo()
      })
    },
    limparAtalhoSelecionado() {
      this.atalhoSelecionado = null
    },
    // Com chip de módulo, a fala sai `/crm <texto>` para o modelo (decisão de
    // produto): é o que a bolha mostra como chip e o que a conversa reaberta relê.
    comComandoDoModulo(texto) {
      const comando = this.ferramentaAtiva ? rotuloDeComandoDoModulo(this.ferramentaAtiva) : ''
      return comando ? comando + ' ' + texto : texto
    },
    // No envio do composer: se a caixa é um atalho do catálogo (com ou sem
    // argumento), despacha o turno do atalho e devolve `true` (o host não
    // manda `send`). Sem host escutando `executar-atalho`, segue o envio normal.
    despacharAtalhoDigitado() {
      if (!this.despachaAtalhoNoEnvio) return false
      this.dispararAtalho(this.atalhoAtivo)
      return true
    },
    dispararAtalho(atalho) {
      const argumento = this.argumentoDoAtalho
      this.atalhoSelecionado = null
      this.$emit('input', '')
      // O atalho é o turno inteiro: um chip de módulo que sobrou não vale para o próximo.
      if (this.ferramentaAtiva) this.$emit('limpar-ferramenta')
      this.$emit('executar-atalho', {
        slug: atalho.id,
        label: atalho.label,
        argumento: argumento || null,
      })
    },
  },
}
